import { z } from "zod";

/**
 * Signup Form Validation Schema
 *
 * Validates:
 * - Email format and presence
 * - Password strength (min 8 chars, uppercase, lowercase, digit)
 * - Password confirmation match
 * - Username length (3-30 chars)
 * - GDPR consent acceptance (required)
 */
export const signupFormSchema = z
  .object({
    email: z
      .string()
      .min(1, "signup.errors.emailRequired")
      .email("signup.errors.emailInvalid"),
    password: z
      .string()
      .min(1, "signup.errors.passwordRequired")
      .min(8, "signup.errors.passwordTooShort")
      .regex(/[A-Z]/, "signup.errors.passwordNoUppercase")
      .regex(/[a-z]/, "signup.errors.passwordNoLowercase")
      .regex(/[0-9]/, "signup.errors.passwordNoDigit"),
    confirmPassword: z.string(),
    username: z
      .string()
      .min(1, "signup.errors.usernameRequired")
      .min(3, "signup.errors.usernameTooShort")
      .max(30, "signup.errors.usernameTooLong"),
    gdprConsent: z.boolean().refine((val) => val === true, {
      message: "signup.errors.consentRequired",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "signup.errors.passwordMismatch",
    path: ["confirmPassword"],
  });

/**
 * Email Verification Code Schema
 *
 * Validates:
 * - Code is exactly 6 characters
 * - Code contains only digits
 */
export const verificationSchema = z.object({
  code: z
    .string()
    .length(6, "signup.verification.errors.codeLength")
    .regex(/^\d+$/, "signup.verification.errors.codeNumeric"),
});

/**
 * Type-safe form data types
 */
export type SignupFormData = z.infer<typeof signupFormSchema>;
export type VerificationFormData = z.infer<typeof verificationSchema>;
